import React, { useCallback, useState } from "react";
import fp from "lodash/fp";
import colorList from "../../components/timeline/colorList";
import TimelineContext from "./TimelineContext";

const defaultTimelineObject = {
    key: "",
    data: [],
    analysisType: "",
    checked: true,
};

const TimelineProvider = (props) => {
    const [maxObjectCount] = useState(10);
    const [timelineObject_, setTimelineObject_] = useState({});
    const [timelineObjectList, setTimelineObjectList] = useState([]);
    const [detailListView, setDetailListView_] = useState(false);
    const [ratio, setRatio_] = useState(1.0);
    const [limit, setLimit_] = useState({
        avgConfidence: 0,
        startTimestamp: 0,
        endTimestamp: null,
    });
    const [activeBlock, setActiveBlock_] = useState({
        object: null,
        key: null,
        analysisType: null,
        index: null,
    });
    function setActiveBlock(object) {
        setActiveBlock_({ ...object });
    }
    function unsetActiveBlock() {
        setActiveBlock_({});
    }

    // {
    //     object: null,
    //     index: null,
    // }
    const [activeLine, setActiveLine_] = useState(null);
    function setActiveLine(object) {
        setActiveLine_({ ...object });
    }
    function unsetActiveLine() {
        setActiveBlock_(null);
    }

    // {
    //     object: null,
    //     color: null,
    //     analysisType: null,
    //     label: null,
    // }
    const [hoverLine, setHoverLine_] = useState(null);
    function setHoverLine(object) {
        setHoverLine_({ ...object });
    }
    function unsetHoverLine() {
        setHoverLine_(null);
    }

    /**
     * @param {string} id
     * @param {object} value
     */
    const setTimelineObject = useCallback(
        (id, value) => {
            if (Object.keys(timelineObject_).length > maxObjectCount) {
                console.debug(`maxObjectCount[${maxObjectCount}] current[${timelineObjectKeys.length}]`);
                return;
            }

            const newValue = Object.assign({ ...defaultTimelineObject }, value);
            timelineObject_[id] = newValue;
            const timelineObjectKeys = Object.keys(timelineObject_);
            const newList = timelineObjectKeys.map((key) => timelineObject_[key]);
            setTimelineObjectList(newList);
        },
        [timelineObject_, timelineObjectList, maxObjectCount],
    );

    const updateTimelineObject = useCallback(
        (id, data) => {
            setTimelineObjectList((prev) => {
                const originObj = prev.find((el) => el.id === id);
                const newObj = { ...originObj, data: data };

                const newTimelineObjectList = prev.map((el) => {
                    if (el.id === id) {
                        return newObj;
                    } else return el;
                });
                return newTimelineObjectList;
            });
        },
        [timelineObject_, timelineObjectList, maxObjectCount],
    );

    const unsetTimelineObject = useCallback(
        (id) => {
            if (fp.isNil(timelineObject_[id])) return;
            delete timelineObject_[id];
            const timelineObjectKeys = Object.keys(timelineObject_);
            const newList = timelineObjectKeys.map((key) => timelineObject_[key]);
            setTimelineObjectList(newList);
        },
        [timelineObject_, timelineObjectList, maxObjectCount],
    );

    const getTimelineObject = useCallback(
        (id, applyLimit = true) => {
            const filteredById = timelineObjectList.filter((o) => o.id === id);
            if (filteredById.length === 0) return null;

            if (!applyLimit) return { ...filteredById[0] };

            const limitStartTimestamp = limit.startTimestamp;
            const limitEndTimestamp = limit.endTimestamp;
            const limitAvgConfidence = limit.avgConfidence;

            const ret = { ...filteredById[0] };

            ret.data = ret.data.filter((analysisDetailData) => {
                return (
                    analysisDetailData.start >= limitStartTimestamp &&
                    (limitEndTimestamp === null ? true : analysisDetailData.end <= limitEndTimestamp) &&
                    analysisDetailData.avgConfidence >= limitAvgConfidence
                );
            });
            return ret;
        },
        [limit, timelineObjectList],
    );

    function getTimelineObjectList(applyLimit = true) {
        if (!applyLimit) return timelineObjectList;

        const limitStartTimestamp = limit.startTimestamp;
        const limitEndTimestamp = limit.endTimestamp;
        const limitAvgConfidence = limit.avgConfidence;
        return timelineObjectList.map((timelineObjectList, index) => {
            return {
                ...timelineObjectList,
                color: colorList[index],
                data: timelineObjectList.data.filter((analysisDetailData) => {
                    return (
                        analysisDetailData.start >= limitStartTimestamp &&
                        (limitEndTimestamp === null ? true : analysisDetailData.end <= limitEndTimestamp) &&
                        analysisDetailData.avgConfidence >= limitAvgConfidence
                    );
                }),
            };
        });
    }

    function timelineObjectCheckToggle(id) {
        const timelineObjectList_ = timelineObjectList.filter((o) => o.id === id);
        if (timelineObjectList_.length === 0) console.warn(`no matched ${id}`);
        timelineObjectList_[0].checked = !timelineObjectList_[0].checked;
        setTimelineObjectList([...timelineObjectList]);
    }

    const setLimit = useCallback(
        (limit) => {
            setLimit_(Object.assign({ ...limit }, limit));
        },
        [limit],
    );

    const timelineContext = {
        limit,
        setLimit,
        activeBlock,
        setActiveBlock,
        unsetActiveBlock,
        activeLine,
        setActiveLine,
        unsetActiveLine,
        hoverLine,
        setHoverLine,
        unsetHoverLine,
        timelineValue: {},
        detailListView,
        detailListViewToggle() {
            setDetailListView_(!detailListView);
        },
        ratio,
        setRatio(ratio_) {
            if (ratio_ < 0) throw new Error("ratio must be greater than 0");
            if (ratio_ > 5.0) throw new Error("ratio must be lower than 5.0");
            setRatio_(ratio_);
        },
        timelineObjectList,
        setTimelineObject,
        unsetTimelineObject,
        timelineObjectCheckToggle,
        getTimelineObject,
        getTimelineObjectList,
        updateTimelineObject,
    };

    return <TimelineContext.Provider value={timelineContext} {...props} />;
};

export default TimelineProvider;
